import { Breakpoints, ButtonNext, ButtonPrevious, styled } from '@volkswagen-onehub/components-core';
import React from 'react';
import { css, keyframes } from 'styled-components';

function useScrollbarWidth(): number {
  const [scrollbarWidth, setScrollbarWidth] = React.useState(0);

  React.useLayoutEffect(() => {
    setScrollbarWidth(window.innerWidth - document.body.clientWidth);
  }, []);

  return scrollbarWidth;
}

export interface TrimSelectionContainerProps {
  readonly children: React.ReactNode;
  readonly sliderElementRef: (element: HTMLElement | null) => void;
  readonly onPrevious?: () => void;
  readonly onNext?: () => void;
  readonly paginationHasBeenUsed: boolean;
}

function getVisibleTrimSelectionColumnCount(viewportWidth: number): number {
  if (viewportWidth < 760) {
    return 1;
  }

  if (viewportWidth < 1280) {
    return 2;
  }

  if (viewportWidth < 1920) {
    return 3;
  }

  return 4;
}

const gridAreas = [
  'image',
  'efficiency',
  'title',
  'prices',
  'buttons',
  'highlightFeaturesHeadline',
  'highlightFeatures',
];

function getGridTemplateAreas(numberOfChildren: number): string {
  const array = [];

  for (const gridArea of gridAreas) {
    let result = '';

    for (let i = 0; i < numberOfChildren; i++) {
      result = `${result} ${gridArea}-${i}`;
    }

    array.push(`"${result.trim()}"`);
  }

  return array.join(' ');
}

const StyledTrimSelectionContainer = styled.div`
  position: relative;
`;

const StyledOverflowContainer = styled.div<{ scrollbarWidth: number }>`
  overflow-x: hidden;
  width: calc(100vw - ${(props) => props.scrollbarWidth}px);
`;

const StyledSliderContainer = styled.div<{ childrenCount: number }>`
  touch-action: pan-y;
  display: grid;

  margin: 0 ${(props) => props.theme.size.grid002} 0 ${(props) => props.theme.size.grid001};

  grid-template-areas: ${(props) => getGridTemplateAreas(props.childrenCount)};

  grid-template-columns: repeat(${(props) => props.childrenCount}, ${100 / getVisibleTrimSelectionColumnCount(759)}%);

  @media (min-width: 760px) {
    grid-template-columns: repeat(${(props) => props.childrenCount}, ${100 / getVisibleTrimSelectionColumnCount(760)}%);
  }

  @media (min-width: ${Breakpoints.b1280}px) {
    grid-template-columns: repeat(
      ${(props) => props.childrenCount},
      ${100 / getVisibleTrimSelectionColumnCount(Breakpoints.b1280)}%
    );
  }

  @media (min-width: ${Breakpoints.b1920}px) {
    grid-template-columns: repeat(
      ${(props) => props.childrenCount},
      ${100 / getVisibleTrimSelectionColumnCount(Breakpoints.b1920)}%
    );
  }
`;

const StyledStickyButtonsContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(2, ${(props) => props.theme.size.grid002});
  justify-content: space-between;
  position: sticky;
  top: calc(50% - 22px);
`;

const StyledButtonsBoundary = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 25vh 0;
  pointer-events: none;
  display: none;

  @media (min-width: ${Breakpoints.b560}px) {
    display: block;
  }
`;

const StyledButtonWrapper = styled.div<{ visible?: boolean }>`
  transform: ${(props) => (props.visible ? 'scale(1)' : 'scale(0)')};
  transition: transform ${(props) => props.theme.animation.timingFunction.in} 300;
  pointer-events: all;
  margin: 0 auto;
`;

const bounceRight = keyframes`
    50% {
      transform: translateX(7px);
  
      @media (min-width: ${Breakpoints.b960}) {
        transform: translateX(15px);
      }
    }`;

const bounceLeft = keyframes`
    50% {
      transform: translateX(-7px);
  
      @media (min-width: ${Breakpoints.b960}) {
        transform: translateX(-15px);
      }
    }`;

const previousButtonAnimation = css`
  animation: ${bounceLeft} 1.3s 3 cubic-bezier(0.445, 0.05, 0.55, 0.95) 5s;
`;

const nextButtonAnimation = css`
  animation: ${bounceRight} 1.3s 3 cubic-bezier(0.445, 0.05, 0.55, 0.95) 5s;
`;

const StyledPreviousButtonWrapper = styled(StyledButtonWrapper)<{
  paginationHasBeenUsed: boolean;
}>`
  text-align: right;
  ${(props) => props.visible && !props.paginationHasBeenUsed && previousButtonAnimation};

  :hover {
    animation: none;
  }
`;

const StyledNextButtonWrapper = styled(StyledButtonWrapper)<{
  paginationHasBeenUsed: boolean;
}>`
  ${(props) => props.visible && !props.paginationHasBeenUsed && nextButtonAnimation};

  :hover {
    animation: none;
  }
`;

export function MofaCarousel({
  children,
  onPrevious,
  onNext,
  sliderElementRef,
  paginationHasBeenUsed,
}: TrimSelectionContainerProps): JSX.Element {
  const scrollbarWidth = useScrollbarWidth();

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (onPrevious && event.key === 'ArrowLeft') {
      onPrevious();
    }

    if (onNext && event.key === 'ArrowRight') {
      onNext();
    }
  };

  return (
    <StyledTrimSelectionContainer onKeyDown={handleKeyDown}>
      <StyledOverflowContainer scrollbarWidth={scrollbarWidth}>
        <StyledSliderContainer ref={sliderElementRef} childrenCount={React.Children.count(children)}>
          {children}
        </StyledSliderContainer>
      </StyledOverflowContainer>
      <StyledButtonsBoundary aria-hidden>
        <StyledStickyButtonsContainer>
          <StyledPreviousButtonWrapper visible={Boolean(onPrevious)} paginationHasBeenUsed={paginationHasBeenUsed}>
            <ButtonPrevious tag="button" emphasis="primary" onClick={onPrevious} tabIndex={-1} />
          </StyledPreviousButtonWrapper>
          <StyledNextButtonWrapper visible={Boolean(onNext)} paginationHasBeenUsed={paginationHasBeenUsed}>
            <ButtonNext
              tag="button"
              emphasis="primary"
              onClick={() => {
                onNext();
              }}
              tabIndex={-1}
            />
          </StyledNextButtonWrapper>
        </StyledStickyButtonsContainer>
      </StyledButtonsBoundary>
    </StyledTrimSelectionContainer>
  );
}
